import { FormQuestion } from './form-question';
import { Draggable, DraggableProvider, reorderItems } from '@components/draggable';
import { Dispatch, StateUpdater, useState } from 'preact/hooks';
import { useUndoRedo } from 'client/lib/hooks';
import { RuzcalFormReadOnlyQuestion } from './form-readonly-question';
import { Button } from '@components/buttons';
import { IcoPlus } from '@components/icons';
import { Case } from '@components/conditional';

import { LoadingIndicator } from '@components/loading-indicator';
import { UndoNotice } from '@course/components/undo-notice';
import { generateUUID } from 'shared/utils';

import { RuzcalFormQuestion } from './types';
import { State } from './types';

type Props = {
  form: State;
  setForm: Dispatch<StateUpdater<State>>;
};

export function QuestionsTab({ form, setForm }: Props) {
  const [openedQuestion, setOpenedQuestion] = useState<UUID | undefined>();
  const [showUndoNotice, setShowUndoNotice] = useState(false);
  const questions = form.questions;
  const activeQuestions = questions == undefined ? [] : questions.filter((q) => !q.isDeleted);

  const undoRedo = useUndoRedo(form, setForm);

  const createNewQuestion = () => {
    const newId = generateUUID();
    const newQuestion: RuzcalFormQuestion = {
      id: newId,
      formId: '',
      title: '',
      type: 'radio',
      seq: 0,
      isRequired: false,
      isDeleted: false,
      answers: [{ id: generateUUID(), questionId: newId, title: '', seq: 0, deleted: false }],
    };

    setForm((s) => ({ ...s, questions: [...s.questions, newQuestion] }));
    setOpenedQuestion(newId);
  };

  if (!questions || questions == undefined) {
    return <LoadingIndicator />;
  }

  return (
    <div class="flex flex-col">
      <DraggableProvider
        canHandleDrop={() => true}
        onDragComplete={() => {}}
        onTargetChange={(dragState) =>
          setForm((s) => {
            return {
              ...s,
              questions: reorderItems(s.questions, dragState),
            };
          })
        }
      >
        {activeQuestions.map((question) => (
          <Draggable key={question.id} id={question.id} table="form-questions">
            <Case
              when={openedQuestion === question.id}
              fallback={
                <RuzcalFormReadOnlyQuestion
                  question={question}
                  onClick={() => setOpenedQuestion(question.id)}
                ></RuzcalFormReadOnlyQuestion>
              }
            >
              <FormQuestion
                question={question}
                onChange={(updates) => {
                  setForm((s) => ({
                    ...s,
                    questions: s.questions.map((q) => (q.id === question.id ? updates : q)),
                  }));
                }}
                onDelete={() => {
                  setShowUndoNotice(true);
                  setForm((s) => ({
                    ...s,
                    questions: s.questions.map((q) =>
                      q.id === question.id ? { ...q, isDeleted: true } : q,
                    ),
                  }));
                }}
              ></FormQuestion>
            </Case>
          </Draggable>
        ))}
      </DraggableProvider>

      <footer class="mt-6">
        <Button
          class="inline-flex items-center text-indigo-600 outline-hidden focus:ring-2 focus:ring-indigo-400 rounded-sm"
          onClick={createNewQuestion}
        >
          <IcoPlus class="h-6 w-6 mr-1 opacity-75" />
          Create a new question
        </Button>
      </footer>
      <UndoNotice
        displayed={showUndoNotice}
        text="Question deleted."
        onClick={undoRedo.undo}
        hide={() => setShowUndoNotice(false)}
      />
    </div>
  );
}
